import { useState } from "react";
import styled from "styled-components";

import { adminTokens } from "@pages/AdminDashboard/theme/adminTokens";
import { Button } from "@pages/AdminDashboard/ui/Button";
import {
  DataTable,
  DataTableColumn,
  DataTablePagination,
} from "@pages/AdminDashboard/ui/DataTable";
import { globalSearchAddress } from "@pages/AdminDashboard/adminRoutes";
import { AdminLink, ExternalLink, IdentifierCell } from "@pages/AdminDashboard/ui/DataTable/cells";
import { StatusBadge } from "@pages/AdminDashboard/ui/StatusBadge";
import { UiKitSection } from "@pages/AdminDashboard/ui/UiKitSection";

interface CaseRow {
  id: string;
  user: string;
  status: string;
  paid: string;
  trip: string;
  clinician: string;
}

interface OrderRow {
  oid: string;
  number: string;
  caseId: string;
  status: string;
  method: string;
  tracking: string;
  paid: string;
  shipsTo: string;
}

// The Find screen's rows, with the design's fake data.
const caseRows: CaseRow[] = [
  {
    id: "#10482",
    user: "test.traveler@example.com",
    status: "completed",
    paid: "✓ Paid",
    trip: "Peru · Oct 12, 2026",
    clinician: "Dr. Test Clinician",
  },
  {
    id: "#10481",
    user: "test.patient.a@example.com",
    status: "processing",
    paid: "✓ Paid",
    trip: "Thailand · Oct 4, 2026",
    clinician: "Dr. Test Clinician",
  },
  {
    id: "#10479",
    user: "test.patient.b@example.com",
    status: "assigned",
    paid: "✓ Paid",
    trip: "Italy · Oct 20, 2026",
    clinician: "Dr. Sample Physician",
  },
  {
    id: "#10476",
    user: "test.patient.c@example.com",
    status: "support",
    paid: "✓ Paid",
    trip: "No trip date",
    clinician: "—",
  },
  {
    id: "#10470",
    user: "test.patient.d@example.com",
    status: "created",
    paid: "✓ Paid",
    trip: "Ghana · Nov 8, 2026",
    clinician: "—",
  },
  {
    id: "#10466",
    user: "test.patient.e@example.com",
    status: "cancelled",
    paid: "Unpaid",
    trip: "Japan · Oct 2, 2026",
    clinician: "—",
  },
];

const orderStatuses = ["in_progress", "out_for_delivery", "completed", "cancelled", "new", "error"];

const orderRows: OrderRow[] = Array.from({ length: 12 }, (_, index) => ({
  oid: `OID b72e-test-${String(index + 1).padStart(4, "0")}`,
  number: `#${88213 - index}`,
  caseId: `#${10482 - index}`,
  status: orderStatuses[index % orderStatuses.length],
  method: index % 2 === 0 ? "ups_second_day_air" : "standard",
  tracking: index % 3 === 0 ? "Not shipped yet" : `1ZTEST${index}0000`,
  paid: "✓ Paid",
  shipsTo: "Miami, FL",
}));

const orderPageSize = 5;
const allRowsPageSize = 30;

const noop = () => undefined;

const [spaceXs, , , , , spaceXl] = adminTokens.spacing;
// The design sets a cell's links 10px apart.
const linkGap = 10;
const { color, typeScale } = adminTokens;

const Example = styled.div.attrs({ role: "group" })`
  display: flex;
  flex-direction: column;
  gap: ${spaceXs}px;
  margin-bottom: ${spaceXl}px;
`;

const Caption = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.meta.fontFamily};
  font-size: ${typeScale.meta.fontSize};
`;

const SoftCell = styled.span`
  color: ${color.inkSoft};
`;

const LinkRow = styled.div`
  display: flex;
  gap: ${linkGap}px;
`;

const caseColumns: DataTableColumn<CaseRow>[] = [
  {
    id: "case",
    header: "Case",
    render: (row) => (
      <IdentifierCell
        primary={row.id}
        secondary={<AdminLink to={globalSearchAddress(row.user) ?? ""}>{row.user}</AdminLink>}
      />
    ),
  },
  {
    id: "status",
    header: "Status",
    render: (row) => <StatusBadge status={row.status} />,
    phonePlacement: "badge",
  },
  { id: "paid", header: "Paid", render: (row) => row.paid },
  { id: "trip", header: "Trip", render: (row) => row.trip },
  { id: "clinician", header: "Clinician", render: (row) => <SoftCell>{row.clinician}</SoftCell> },
  {
    id: "links",
    header: "Links",
    render: () => (
      <LinkRow>
        <ExternalLink href="https://example.com/consult">Consult</ExternalLink>
        <ExternalLink href="https://example.com/invoice">Invoice</ExternalLink>
      </LinkRow>
    ),
  },
  {
    id: "actions",
    header: "Actions",
    align: "end",
    isHeaderHidden: true,
    render: () => <Button look="secondary">Show conditions</Button>,
  },
];

const orderColumns: DataTableColumn<OrderRow>[] = [
  {
    id: "order",
    header: "Order",
    render: (row) => <IdentifierCell primary={row.oid} secondary={row.number} />,
  },
  { id: "case", header: "Case", render: (row) => row.caseId },
  {
    id: "status",
    header: "Status",
    render: (row) => <StatusBadge status={row.status} />,
    phonePlacement: "badge",
  },
  { id: "method", header: "Shipping method", render: (row) => row.method },
  { id: "tracking", header: "Tracking", render: (row) => <SoftCell>{row.tracking}</SoftCell> },
  { id: "paid", header: "Paid", render: (row) => row.paid },
  { id: "shipsTo", header: "Ships to", render: (row) => <SoftCell>{row.shipsTo}</SoftCell> },
  {
    id: "actions",
    header: "Actions",
    align: "end",
    isHeaderHidden: true,
    render: () => <Button look="secondary">Open →</Button>,
  },
];

const caseEmptyState = {
  title: "No Cases match these filters",
  message: "Status support, created Sep 1 – Sep 3, 2026. Widen the dates or clear the status.",
  actionLabel: "Clear filters",
  onAction: noop,
};

const firstPage: DataTablePagination = {
  offset: 0,
  pageSize: allRowsPageSize,
  orderLabel: "newest first",
  onPrevious: noop,
  onNext: noop,
};

const PaginatedOrders = () => {
  const [offset, setOffset] = useState(0);

  return (
    <DataTable
      label="Orders, paginated"
      columns={orderColumns}
      rows={orderRows.slice(offset, offset + orderPageSize)}
      getRowKey={(row) => row.oid}
      emptyState={{ title: "No Orders match", message: "Nothing matches this search." }}
      pagination={{
        offset,
        pageSize: orderPageSize,
        orderLabel: "newest first",
        onPrevious: () => setOffset(offset - orderPageSize),
        onNext: () => setOffset(offset + orderPageSize),
      }}
    />
  );
};

export const DataTableSection = () => (
  <UiKitSection title="Data table">
    <Example aria-label="Filled">
      <Caption>Filled</Caption>
      <DataTable
        label="Cases, filled"
        columns={caseColumns}
        rows={caseRows}
        getRowKey={(row) => row.id}
        selectedRowKey="#10481"
        emptyState={caseEmptyState}
        pagination={firstPage}
      />
    </Example>
    <Example aria-label="Paginated">
      <Caption>Paginated</Caption>
      <PaginatedOrders />
    </Example>
    <Example aria-label="Empty">
      <Caption>Empty</Caption>
      <DataTable
        label="Cases, empty"
        columns={caseColumns}
        rows={[]}
        getRowKey={(row) => row.id}
        emptyState={caseEmptyState}
        pagination={firstPage}
      />
    </Example>
    <Example aria-label="Loading">
      <Caption>Loading</Caption>
      <DataTable
        label="Cases, loading"
        columns={caseColumns}
        rows={[]}
        getRowKey={(row) => row.id}
        isLoading
        emptyState={caseEmptyState}
        pagination={firstPage}
      />
    </Example>
    <Example aria-label="Failed">
      <Caption>Failed</Caption>
      <DataTable
        label="Cases, failed"
        columns={caseColumns}
        rows={caseRows.slice(0, 3)}
        getRowKey={(row) => row.id}
        error={{
          title: "The case search failed.",
          body: "Your filters are kept; nothing changed.",
          retryLabel: "Try again",
          onRetry: noop,
        }}
        emptyState={caseEmptyState}
        pagination={firstPage}
      />
    </Example>
  </UiKitSection>
);
